import { type DateTime } from "luxon";

import { type CarouselTaskItem } from "../TaskDashboardTemplate/taskUtils";
import { DEFAULT_TASK_DURATION_MINUTES, taskDueDateTime } from "./taskCalendarUtils";

/** Spacing between normalized starts; matches default calendar block duration. */
export const NORMALIZE_SLOT_MINUTES = DEFAULT_TASK_DURATION_MINUTES;

export const TASK_CALENDAR_SCHEDULE_PREFS_KEY = "taskCalendar.schedulePrefs";
export const TASK_CALENDAR_NORMALIZE_ENABLED_KEY = "taskCalendar.normalizeEnabled";

/** Calendar week column order: index 0 = Sunday … 6 = Saturday (matches Luxon week with Sunday start). */
export const TASK_CALENDAR_SCHEDULE_DAY_COUNT = 7;

export const TASK_CALENDAR_SCHEDULE_DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

export interface DaySchedulePrefs {
  /** When false, the full local calendar day is non-working for normalization and shading. */
  workingDay: boolean;
  /**
   * Minutes from midnight [0, 1440).
   * Same calendar day: working is [workdayStartMin, workdayEndMin) when start &lt; end.
   * Overnight: when start &gt; end, working is [start, 1440) ∪ [0, end).
   */
  workdayStartMin: number;
  workdayEndMin: number;
  /** When lunchStartMin &lt; lunchEndMin, this range is non-working; must lie inside one work segment. */
  lunchStartMin: number;
  lunchEndMin: number;
}

export interface TaskCalendarSchedulePrefs {
  days: DaySchedulePrefs[];
}

const MINUTES_PER_DAY = 24 * 60;

/** Max minute walk when snapping to the next working instant (one full week). */
const SNAP_MAX_MINUTES = MINUTES_PER_DAY * 7;

function clampMinute(n: number): number {
  if (!Number.isFinite(n)) {
    return 0;
  }
  return Math.max(0, Math.min(1439, Math.round(n)));
}

function lunchFitsWindow(s: number, e: number, ls: number, le: number): boolean {
  if (ls >= le) {
    return true;
  }
  if (s < e) {
    return ls >= s && le <= e;
  }
  if (s > e) {
    const evening = ls >= s && le <= MINUTES_PER_DAY;
    const morning = ls >= 0 && le <= e;
    return evening || morning;
  }
  return false;
}

export function sanitizeDaySchedule(d: DaySchedulePrefs): DaySchedulePrefs {
  if (!d.workingDay) {
    return {
      lunchEndMin: 0,
      lunchStartMin: 0,
      workdayEndMin: clampMinute(d.workdayEndMin),
      workdayStartMin: clampMinute(d.workdayStartMin),
      workingDay: false,
    };
  }
  let s = clampMinute(d.workdayStartMin);
  let e = clampMinute(d.workdayEndMin);
  if (s === e) {
    s = 9 * 60;
    e = 17 * 60;
  }
  let ls = clampMinute(d.lunchStartMin);
  let le = clampMinute(d.lunchEndMin);
  if (ls >= le || !lunchFitsWindow(s, e, ls, le)) {
    ls = 0;
    le = 0;
  }
  return {
    lunchEndMin: le,
    lunchStartMin: ls,
    workdayEndMin: e,
    workdayStartMin: s,
    workingDay: true,
  };
}

function defaultDaysArray(): DaySchedulePrefs[] {
  const off = sanitizeDaySchedule({
    lunchEndMin: 0,
    lunchStartMin: 0,
    workdayEndMin: 17 * 60,
    workdayStartMin: 9 * 60,
    workingDay: false,
  });
  const work = sanitizeDaySchedule({
    lunchEndMin: 13 * 60,
    lunchStartMin: 12 * 60,
    workdayEndMin: 17 * 60,
    workdayStartMin: 9 * 60,
    workingDay: true,
  });
  return [off, work, work, work, work, work, off];
}

export const DEFAULT_TASK_CALENDAR_SCHEDULE_PREFS: TaskCalendarSchedulePrefs = {
  days: defaultDaysArray(),
};

function cloneDefaultPrefs(): TaskCalendarSchedulePrefs {
  return { days: defaultDaysArray().map((d) => ({ ...d })) };
}

export function sanitizeSchedulePrefs(p: TaskCalendarSchedulePrefs): TaskCalendarSchedulePrefs {
  const defaults = defaultDaysArray();
  const raw = Array.isArray(p.days) ? p.days : [];
  const days: DaySchedulePrefs[] = [];
  for (let i = 0; i < TASK_CALENDAR_SCHEDULE_DAY_COUNT; i++) {
    const base = defaults[i];
    const src = raw[i];
    if (!src || typeof src !== "object") {
      days.push({ ...base });
      continue;
    }
    days.push(
      sanitizeDaySchedule({
        lunchEndMin: Number.isFinite(Number(src.lunchEndMin)) ? Number(src.lunchEndMin) : base.lunchEndMin,
        lunchStartMin: Number.isFinite(Number(src.lunchStartMin)) ? Number(src.lunchStartMin) : base.lunchStartMin,
        workdayEndMin: Number.isFinite(Number(src.workdayEndMin)) ? Number(src.workdayEndMin) : base.workdayEndMin,
        workdayStartMin: Number.isFinite(Number(src.workdayStartMin))
          ? Number(src.workdayStartMin)
          : base.workdayStartMin,
        workingDay: typeof src.workingDay === "boolean" ? src.workingDay : base.workingDay,
      }),
    );
  }
  return { days };
}

function migrateLegacyFlatPrefs(o: Record<string, unknown>): TaskCalendarSchedulePrefs {
  const excludeWeekends = typeof o.excludeWeekends === "boolean" ? o.excludeWeekends : true;
  const ws = Number(o.workdayStartMin);
  const we = Number(o.workdayEndMin);
  let s = Number.isFinite(ws) ? clampMinute(ws) : 9 * 60;
  let e = Number.isFinite(we) ? clampMinute(we) : 17 * 60;
  if (s >= e) {
    s = 9 * 60;
    e = 17 * 60;
  }
  let ls = clampMinute(Number(o.lunchStartMin));
  let le = clampMinute(Number(o.lunchEndMin));
  if (ls >= le || !lunchFitsWindow(s, e, ls, le)) {
    ls = 0;
    le = 0;
  }
  const working: DaySchedulePrefs = {
    lunchEndMin: le,
    lunchStartMin: ls,
    workdayEndMin: e,
    workdayStartMin: s,
    workingDay: true,
  };
  const off: DaySchedulePrefs = {
    lunchEndMin: 0,
    lunchStartMin: 0,
    workdayEndMin: 17 * 60,
    workdayStartMin: 9 * 60,
    workingDay: false,
  };
  const days: DaySchedulePrefs[] = [];
  for (let i = 0; i < TASK_CALENDAR_SCHEDULE_DAY_COUNT; i++) {
    const weekend = i === 0 || i === 6;
    days.push(weekend && excludeWeekends ? { ...off } : { ...working });
  }
  return { days };
}

export function parseSchedulePrefs(raw: unknown): TaskCalendarSchedulePrefs {
  if (!raw || typeof raw !== "object") {
    return cloneDefaultPrefs();
  }
  const o = raw as Record<string, unknown>;
  if (Array.isArray(o.days) && o.days.length === TASK_CALENDAR_SCHEDULE_DAY_COUNT) {
    return sanitizeSchedulePrefs({ days: o.days as DaySchedulePrefs[] });
  }
  if ("workdayStartMin" in o || "workdayEndMin" in o) {
    return sanitizeSchedulePrefs(migrateLegacyFlatPrefs(o));
  }
  return cloneDefaultPrefs();
}

export function loadSchedulePrefs(): TaskCalendarSchedulePrefs {
  try {
    const s = localStorage.getItem(TASK_CALENDAR_SCHEDULE_PREFS_KEY);
    if (!s) {
      return cloneDefaultPrefs();
    }
    return parseSchedulePrefs(JSON.parse(s) as unknown);
  } catch {
    return cloneDefaultPrefs();
  }
}

export function saveSchedulePrefs(prefs: TaskCalendarSchedulePrefs): void {
  try {
    localStorage.setItem(TASK_CALENDAR_SCHEDULE_PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // ignore quota / private mode
  }
}

export function loadNormalizeEnabled(): boolean {
  try {
    return localStorage.getItem(TASK_CALENDAR_NORMALIZE_ENABLED_KEY) === "1";
  } catch {
    return false;
  }
}

export function saveNormalizeEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(TASK_CALENDAR_NORMALIZE_ENABLED_KEY, enabled ? "1" : "0");
  } catch {
    // ignore
  }
}

export function minutesToTimeInputValue(minutes: number): string {
  const m = clampMinute(minutes);
  const h = Math.floor(m / 60);
  const min = m % 60;
  return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
}

export function timeInputValueToMinutes(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) {
    return null;
  }
  const h = Number(match[1]);
  const min = Number(match[2]);
  if (!Number.isFinite(h) || !Number.isFinite(min) || h > 23 || min > 59) {
    return null;
  }
  return h * 60 + min;
}

/** Map Luxon weekday (Mon=1 … Sun=7) to {@link TaskCalendarSchedulePrefs.days} index (Sun=0 … Sat=6). */
export function luxonWeekdayToScheduleDayIndex(dt: DateTime): number {
  return dt.weekday === 7 ? 0 : dt.weekday;
}

function minuteInWorkSegments(m: number, s: number, e: number): boolean {
  if (s < e) {
    return m >= s && m < e;
  }
  if (s > e) {
    return m >= s || m < e;
  }
  return false;
}

export function isWorkingInstant(dt: DateTime, p: TaskCalendarSchedulePrefs): boolean {
  const idx = luxonWeekdayToScheduleDayIndex(dt);
  const day = p.days[idx];
  if (!day?.workingDay) {
    return false;
  }
  const m = dt.hour * 60 + dt.minute;
  const { workdayStartMin: s, workdayEndMin: e, lunchStartMin: ls, lunchEndMin: le } = day;
  if (!minuteInWorkSegments(m, s, e)) {
    return false;
  }
  if (ls < le && m >= ls && m < le) {
    return false;
  }
  return true;
}

export interface MinuteInterval {
  end: number;
  start: number;
}

function mergeMinuteIntervals(intervals: MinuteInterval[]): MinuteInterval[] {
  intervals.sort((a, b) => a.start - b.start);
  const merged: MinuteInterval[] = [];
  for (const iv of intervals) {
    if (iv.start >= iv.end) {
      continue;
    }
    const last = merged[merged.length - 1];
    if (!last || iv.start > last.end) {
      merged.push({ end: iv.end, start: iv.start });
    } else {
      last.end = Math.max(last.end, iv.end);
    }
  }
  return merged;
}

/**
 * Half-open [start, end) minute ranges from local midnight that are **non-working** for `day`, matching
 * {@link isWorkingInstant}. For calendar background shading.
 */
export function getNonWorkingMinuteIntervals(day: DateTime, p: TaskCalendarSchedulePrefs): MinuteInterval[] {
  const d = day.startOf("day");
  const idx = luxonWeekdayToScheduleDayIndex(d);
  const dayPrefs = p.days[idx];
  if (!dayPrefs.workingDay) {
    return [{ end: MINUTES_PER_DAY, start: 0 }];
  }
  const { workdayStartMin: s, workdayEndMin: e, lunchStartMin: ls, lunchEndMin: le } = dayPrefs;
  const intervals: MinuteInterval[] = [];
  if (s < e) {
    if (s > 0) {
      intervals.push({ end: s, start: 0 });
    }
    if (e < MINUTES_PER_DAY) {
      intervals.push({ end: MINUTES_PER_DAY, start: e });
    }
  } else if (s > e) {
    intervals.push({ end: s, start: e });
  }
  if (ls < le) {
    intervals.push({ end: le, start: ls });
  }
  return mergeMinuteIntervals(intervals);
}

/** When a clock hour is entirely non-working, its row height is the base hour height divided by this. */
export const FULLY_NONWORKING_HOUR_ROW_DIVISOR = 5;

export function hourIsFullyNonWorkingInIntervals(hour: number, intervals: MinuteInterval[]): boolean {
  const start = hour * 60;
  const end = start + 60;
  return intervals.some((iv) => iv.start <= start && iv.end >= end);
}

/**
 * Pixel height for each clock-hour row (length 24). Rows use `baseHourRowPx / FULLY_NONWORKING_HOUR_ROW_DIVISOR` when
 * that hour is fully non-working on **every** day in `days` so week columns stay aligned.
 */
export function getHourRowHeightsPx(
  days: DateTime[],
  prefs: TaskCalendarSchedulePrefs,
  baseHourRowPx: number,
): number[] {
  const compressed = baseHourRowPx / FULLY_NONWORKING_HOUR_ROW_DIVISOR;
  const dayStarts = days.map((dt) => dt.startOf("day"));
  const heights: number[] = [];
  for (let hour = 0; hour < 24; hour++) {
    const greyOnAllDays = dayStarts.every((dt) =>
      hourIsFullyNonWorkingInIntervals(hour, getNonWorkingMinuteIntervals(dt, prefs)),
    );
    heights.push(greyOnAllDays ? compressed : baseHourRowPx);
  }
  return heights;
}

export function totalDayGridHeightPx(rowHeights: number[]): number {
  return rowHeights.reduce((sum, h) => sum + h, 0);
}

/** Linear Y offset (px) for a time-of-day; supports fractional minutes (e.g. “now” line). */
export function minuteOfDayToYpx(rowHeights: number[], minuteOfDay: number): number {
  const total = totalDayGridHeightPx(rowHeights);
  if (minuteOfDay <= 0) {
    return 0;
  }
  if (minuteOfDay >= MINUTES_PER_DAY) {
    return total;
  }
  const hour = Math.floor(minuteOfDay / 60);
  const m = minuteOfDay - hour * 60;
  let y = 0;
  for (let i = 0; i < hour; i++) {
    y += rowHeights[i];
  }
  y += (m / 60) * rowHeights[hour];
  return y;
}

export function minuteSpanToPercentOfDay(
  rowHeights: number[],
  startMin: number,
  endMin: number,
): { heightPct: number; topPct: number } {
  const total = totalDayGridHeightPx(rowHeights);
  if (total <= 0) {
    return { heightPct: 0, topPct: 0 };
  }
  const y0 = minuteOfDayToYpx(rowHeights, startMin);
  const y1 = minuteOfDayToYpx(rowHeights, endMin);
  return {
    heightPct: ((y1 - y0) / total) * 100,
    topPct: (y0 / total) * 100,
  };
}

export function snapToNextWorkingInstant(t: DateTime, p: TaskCalendarSchedulePrefs): DateTime {
  let cur = t.startOf("minute");
  for (let i = 0; i < SNAP_MAX_MINUTES; i++) {
    if (isWorkingInstant(cur, p)) {
      return cur;
    }
    cur = cur.plus({ minutes: 1 });
  }
  return t.startOf("minute");
}

/** Add wall-clock minutes, then if the result falls on non-working time, jump to the next working instant. */
export function addWallMinutesSkippingNonWorking(
  from: DateTime,
  wallMinutes: number,
  p: TaskCalendarSchedulePrefs,
): DateTime {
  let cur = from.plus({ minutes: wallMinutes }).startOf("minute");
  let guard = 0;
  while (!isWorkingInstant(cur, p) && guard++ < SNAP_MAX_MINUTES) {
    cur = snapToNextWorkingInstant(cur, p);
  }
  return cur;
}

/** Earliest normalized slot: current minute if working, otherwise next working instant (never in the past). */
export function anchorNormalizationStart(now: DateTime, prefs: TaskCalendarSchedulePrefs): DateTime {
  const t = now.startOf("minute");
  if (isWorkingInstant(t, prefs)) {
    return t;
  }
  return snapToNextWorkingInstant(t, prefs);
}

/**
 * Only **past-due** tasks (`due` strictly before `fromTime`) are normalized. Future and current tasks are omitted from
 * the map and keep their real due times on the calendar.
 *
 * Past tasks are sorted by original due (soonest first). Display starts run **from `fromTime` forward**: first slot at
 * {@link anchorNormalizationStart}, then each next {@link NORMALIZE_SLOT_MINUTES} along working time.
 */
export function buildNormalizedDisplayDueByTaskId(
  tasks: CarouselTaskItem[],
  prefs: TaskCalendarSchedulePrefs,
  normalizeEnabled: boolean,
  fromTime: DateTime,
): Map<string, DateTime> {
  const out = new Map<string, DateTime>();
  if (!normalizeEnabled) {
    return out;
  }

  const fromMs = fromTime.toMillis();

  const dated = tasks.filter((t): t is CarouselTaskItem & { id: string } => {
    if (!t.id) {
      return false;
    }
    const due = taskDueDateTime(t);
    if (!due) {
      return false;
    }
    return due.toMillis() < fromMs;
  });

  dated.sort((a, b) => {
    const da = taskDueDateTime(a)?.toMillis() ?? 0;
    const db = taskDueDateTime(b)?.toMillis() ?? 0;
    if (da !== db) {
      return da - db;
    }
    return a.id.localeCompare(b.id);
  });

  if (dated.length === 0) {
    return out;
  }

  const step = NORMALIZE_SLOT_MINUTES;
  let cursor = anchorNormalizationStart(fromTime, prefs);

  for (const task of dated) {
    out.set(task.id, cursor);
    cursor = addWallMinutesSkippingNonWorking(cursor, step, prefs);
  }

  return out;
}
